import { useTranslation } from 'react-i18next';
import { useActiveLocation } from '../../hooks/useActiveLocation';
import { useGrantedPermissions, useHasPermission } from '../../hooks/useHasPermission';
import { PERMISSIONS } from '../../constants/permissions';
import { useNavOverflow } from '../../hooks/useNavOverflow';
import ROUTES, {
  LOCATION_SEGMENTS,
  LOCATION_VIEW_PERMISSIONS,
  locationPath,
  type LocationSegment
} from '../../routes/routeConstants';
import { HeaderNavGhost } from './HeaderNavGhost';
import { HeaderNavItem, type NavItem } from './HeaderNavItem';
import { HeaderNavMore } from './HeaderNavMore';
import styles from './HeaderNav.module.css';

// Persistent top navigation (replaces the old hamburger side drawer). Every
// page the user can reach is listed, and the list folds into "More" from the
// end only when the bar runs out of room.
export const HeaderNav = () => {
  const { t } = useTranslation();
  const { activeLocationId } = useActiveLocation();
  // One permission per gated destination, the same one its route guard checks:
  // a location view reads its own data at the location, bookings read the
  // location's bookings, the builder edits the account's shared templates.
  const grantedViewPermissions = useGrantedPermissions(Object.values(LOCATION_VIEW_PERMISSIONS));
  const canReadBookings = useHasPermission(PERMISSIONS.BookingRead);
  const canEditConfiguration = useHasPermission(PERMISSIONS.TreatmentPlanTemplatesWrite);

  const atLocation = (...segments: string[]): string | null =>
    activeLocationId ? locationPath(activeLocationId, ...segments) : null;

  const canOpenView = (segment: LocationSegment): boolean =>
    grantedViewPermissions.includes(LOCATION_VIEW_PERMISSIONS[segment]);

  // Ordered by how often the page is opened during a planning day, so the
  // least used ones are the first to fold away.
  const allItems: NavItem[] = [
    { labelKey: 'nav-home', to: `/${ROUTES.locations}`, isExactMatch: true },
    {
      labelKey: 'nav-year-overview',
      to: atLocation(LOCATION_SEGMENTS.yearOverview),
      isVisible: canOpenView(LOCATION_SEGMENTS.yearOverview)
    },
    {
      labelKey: 'nav-week-overview',
      to: atLocation(LOCATION_SEGMENTS.weekOverview),
      isVisible: canOpenView(LOCATION_SEGMENTS.weekOverview)
    },
    {
      labelKey: 'nav-patients',
      to: atLocation(LOCATION_SEGMENTS.patients),
      isVisible: canOpenView(LOCATION_SEGMENTS.patients)
    },
    {
      labelKey: 'nav-economics',
      to: atLocation(LOCATION_SEGMENTS.economics),
      isVisible: canOpenView(LOCATION_SEGMENTS.economics)
    },
    { labelKey: 'nav-bookings', to: atLocation(ROUTES.bookings), isVisible: canReadBookings },
    {
      labelKey: 'nav-treatment-plan-builder',
      to: `/${ROUTES.configuration}`,
      isVisible: canEditConfiguration
    }
  ];

  // Pages the current user has no access to are removed entirely, not
  // disabled: a page they can never reach is noise, not information.
  const items = allItems.filter((item) => item.isVisible !== false);

  const { navRef, ghostRef, visibleCount } = useNavOverflow(items.length);

  const barItems = items.slice(0, visibleCount);
  const foldedItems = items.slice(visibleCount);

  return (
    <nav ref={navRef} className={styles.nav} aria-label={t('navigation')}>
      <HeaderNavGhost items={items} listRef={ghostRef} />
      <ul className={styles.navList}>
        {barItems.map((item) => (
          <HeaderNavItem key={item.labelKey} item={item} />
        ))}
        {foldedItems.length > 0 && (
          <li>
            <HeaderNavMore items={foldedItems} />
          </li>
        )}
      </ul>
    </nav>
  );
};
